/**
 * Award celebration (Sep 2026): the first time a user sees a Drivers award HR
 * has released to them — on the hub landing or in the Trophy Cabinet — the page
 * throws confetti once. Remembered per browser (localStorage), like the other
 * "seen" UI memory.
 *
 * Only recent awards count (released for one of the last two months), so the
 * first visit after this shipped doesn't replay a year of old wins.
 */
import type { CabinetStar } from '@daaily/shared';

/** Per signed-in user, so a shared browser or impersonation never spends someone else's celebration. */
const key = (email: string) => `daaily.driversCelebrated:${email.toLowerCase()}`;
const starKey = (s: Pick<CabinetStar, 'month' | 'valueId'>) => `${s.month}:${s.valueId}`;

function celebrated(email: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(key(email)) || '[]') as string[]); } catch { return new Set(); }
}

function recentCutoff(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 2);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Recent awards the user hasn't been celebrated for yet. */
export function uncelebratedStars(email: string, stars: CabinetStar[]): CabinetStar[] {
  if (!email) return [];
  const done = celebrated(email);
  const cutoff = recentCutoff();
  return stars.filter((s) => s.month >= cutoff && !done.has(starKey(s)));
}

export function markCelebrated(email: string, stars: CabinetStar[]) {
  if (!email) return;
  const done = celebrated(email);
  for (const s of stars) done.add(starKey(s));
  try { localStorage.setItem(key(email), JSON.stringify([...done].slice(-200))); } catch { /* ignore */ }
}
